import { CellContext } from './types';

export type GridCellContext<T> = {
  cellId: string;
  rowId: string;
  field: string;
} & CellContext<T>;

export type GridMiddlewareContext<TArgs extends any[], TResult, CellState> = {
  args: TArgs;
  cellContext: GridCellContext<CellState>;
  proceed: (...args: TArgs) => Promise<TResult>;
  abort: (error?: Error) => never;
  metadata: Record<string, any>;
};

export type GridCellMiddleware<TArgs extends any[], TResult, CellState> = (
  context: GridMiddlewareContext<TArgs, TResult, CellState>,
) => Promise<TResult>;

export function createGridCellMiddlewareChain<TArgs extends any[], TResult, CellState>(
  middlewares: GridCellMiddleware<TArgs, TResult, CellState>[],
  { updateCellState, clearCellState }: CellContext<CellState>,
  originalHandler: (...args: TArgs) => Promise<TResult>,
) {
  return (cellId: string, rowId: string, field: string) => {
    return async (...args: TArgs): Promise<TResult> => {
      const metadata: Record<string, any> = {};

      const cellContext: GridCellContext<CellState> = {
        cellId,
        rowId,
        field,
        updateCellState,
        clearCellState,
      };

      const composedHandler = middlewares.reduceRight((next, middleware) => {
        return async (...middlewareArgs: TArgs): Promise<TResult> => {
          const context: GridMiddlewareContext<TArgs, TResult, CellState> = {
            args: middlewareArgs,
            cellContext,
            proceed: next,
            abort: (error?: Error) => {
              throw error || new Error('Middleware aborted');
            },
            metadata,
          };
          return middleware(context);
        };
      }, originalHandler);

      return composedHandler(...args);
    };
  };
}

export function enhanceGridCellHandler<TArgs extends any[], TResult, CellState>(
  originalHandler: (...args: TArgs) => Promise<TResult>,
  cellContext: CellContext<CellState>,
  middlewares: GridCellMiddleware<TArgs, TResult, CellState>[],
) {
  const chain = createGridCellMiddlewareChain(middlewares, cellContext, originalHandler);

  return {
    createEnhancedHandler: chain,
    withCellId: (cellId: string, rowId: string, field: string) => chain(cellId, rowId, field),
  };
}
